import React, {useState, useEffect, useRef} from 'react'; // Added useRef
import './App.css';
import { checkAuthToken } from './AuthApi';

// Define the useFitText hook
const useFitText = (ref, { initialMaxVhFontSize, minPxFontSize = 8 } = {}, externalDeps = []) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const adjustFontSize = () => {
      if (!element.parentElement || typeof window === 'undefined') return;

      const maxHeight = element.clientHeight;
      const maxWidth = element.clientWidth;

      let currentFontSize;
      if (initialMaxVhFontSize) {
        currentFontSize = (initialMaxVhFontSize / 100) * window.innerHeight;
      } else {
        currentFontSize = 50; // Default starting max font size in px
      }
      
      // Ensure currentFontSize is a number before applying
      if (isNaN(currentFontSize)) {
        currentFontSize = 16; // Fallback to a default pixel size
      }
      element.style.fontSize = currentFontSize + 'px';

      // Iteratively reduce font size until content fits
      while (
        (element.scrollHeight > maxHeight || element.scrollWidth > maxWidth) &&
        currentFontSize > minPxFontSize
      ) {
        currentFontSize -= 1; // Decrement by 1px
        element.style.fontSize = currentFontSize + 'px';
      }
    };

    const debounce = (func, delay) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    };

    const debouncedAdjustFontSize = debounce(adjustFontSize, 200); // 200ms debounce

    const initialTimeoutId = setTimeout(adjustFontSize, 50); // Delay for initial render

    window.addEventListener('resize', debouncedAdjustFontSize);

    return () => {
      clearTimeout(initialTimeoutId);
      window.removeEventListener('resize', debouncedAdjustFontSize);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, initialMaxVhFontSize, minPxFontSize, ...externalDeps]);
};


const LandingPage = () => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const validateAuth = async () => {
      const isValid = await checkAuthToken();  // Check if user has a valid token
      setIsAuthorized(isValid);  // Update authorization state
    };
    validateAuth();
  }, []); // Added empty dependency array to prevent continuous re-renders

  // Check if device is mobile based on screen width
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768); // Common breakpoint for mobile devices
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Completely disable scrolling when landing page is mounted
  useEffect(() => {
    // Save the original overflow style
    const originalStyle = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalHeight = document.body.style.height;
    
    // Disable scrolling completely
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.position = originalPosition;
      document.body.style.height = originalHeight;
    };
  }, []);

  const headingContainerRef = useRef(null);
  const descriptionContainerRef = useRef(null);
  const socialGoodContentRef = useRef(null);

  // Apply the useFitText hook
  // Values for initialMaxVhFontSize are derived from your previous clamp() attempts
  useFitText(headingContainerRef, { initialMaxVhFontSize: 5, minPxFontSize: 10 });
  useFitText(descriptionContainerRef, { initialMaxVhFontSize: 3.4, minPxFontSize: 8 });
  useFitText(socialGoodContentRef, { initialMaxVhFontSize: 2.6, minPxFontSize: 8 });

  return (
    <div className="landing-page">
      <div className="landing-container" style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="landing-content">
          <div className="top-spacer" style={{marginTop: '5vh'}}></div>
          
          <div className="logo-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto',
            maxWidth: '80vw',
          }}>
            <img 
              src="logo_blue_cogitat_ubuntu_sans.png"
              alt="Cogitat's Logo" 
              className="model-logo"
              style={{
                maxHeight: '48vh',
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                transition: 'transform 0.3s ease'
              }}
              onClick={() => {
                if (isAuthorized) {
                  // Force App component to rerender by toggling a value in localStorage
                  localStorage.setItem('rerender', Date.now().toString());
                  window.location.href = '/vault';
                }
                else {
                  window.location.href = '/clients';
                }
              }}
            />
          </div> {/* End .logo-container */}
          
          <div ref={headingContainerRef} className="heading-container" style={{
            height: '9vh',
            textAlign: 'center',
            margin: `${isMobile ? '2vh' : '2vh'} auto ${isMobile ? '0' : '-2vh'}`,
            fontWeight: '600',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div className="heading-text">Optimizing Capital Allocation</div>
          </div> {/* End .heading-container */}
          
          <div ref={descriptionContainerRef} className="description-container" style={{
            height: '14vh',
            textAlign: 'center',
            margin: '0vh auto 0',
            fontWeight: '300',
            lineHeight: '1.35',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            Cogitat develops Artificial Intelligence solutions to assist individuals, small businesses and corporations deploy capital more effectively for long-term value creation.
          </div> {/* End .description-container */}
          <social-good> 
            <div ref={socialGoodContentRef} className='social-good-content' style={{
            height: '10vh',
            textAlign: 'center',
            margin: `${isMobile ? '3vh' : '-2vh'} auto 0`,
            fontWeight: '300',
            lineHeight: '1.35',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
              When done well, capital allocation is a public good. It funds innovation, creates jobs, and strengthens economies.
            </div> {/* End .social-good-content */}
          </social-good>
        </div> {/* End .landing-content */}
      </div> {/* End .landing-container */}
      <footer style={{
        textAlign: 'center', 
        fontSize: '0.9em', 
        color: '#555',
        position: 'fixed',
        bottom: '0',
        left: '0',
        width: '100%',
        padding: '1rem 0',
        backgroundColor: 'rgba(255, 255, 255, 0.9)'
      }}>
        <div className="footer-content" style={{display: 'inline-flex', alignItems: 'center', gap: '0.4em', flexWrap: 'wrap', justifyContent: 'center'}}>
          <span>Cogitat Inc.</span>
          <span style={{color: '#999'}}>·</span>
          <span>Kitchener-Waterloo, Ontario</span>
          <img 
            src="canadian_flag_grayscale.svg" 
            alt="Canadian Flag" 
            style={{height: '1em', opacity: '0.75', verticalAlign: 'middle'}}
          />
          <span style={{color: '#333', fontWeight: '500'}}> Proudly Canadian</span>
        </div> {/* End .footer-content */}
      </footer>
    </div> /* End .landing-page */
  );
};

export default LandingPage;